import { api } from '@goaltree/backend/convex/_generated/api'
import {
  allowedCoachStyle,
  coachDisplayName,
  coachNameOverride,
  isFallingBehind,
  resolveDynamicCoachStyle,
} from '@goaltree/backend/convex/lib/coach'
import { useConvexAuth, useQuery } from 'convex/react'
import { useTranslation } from 'react-i18next'

import { useGoals } from '@/hooks/use-goals'
import { useIsPro } from '@/hooks/use-is-pro'

// The tone the coach is using right now (an adaptive coach turns strict when
// the user is falling behind), localized.
export function useCoachStyleLabel(): string {
  const { t } = useTranslation()
  const { isAuthenticated } = useConvexAuth()
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip')
  const streakInfo = useQuery(api.nodes.getUserStreakInfo, isAuthenticated ? {} : 'skip')
  const goals = useGoals()
  const { isPro, isLoading: proLoading } = useIsPro()
  const p = prefs as { coachStyle?: string; lifestyle?: string } | undefined
  const behind = isFallingBehind(
    (goals ?? [])
      .filter((g) => !g.isInbox && g.status !== 'done')
      .map((g) => ({ progress: g.progress, remaining: g.remainingCount })),
    streakInfo?.streak ?? 0,
  )
  const style = resolveDynamicCoachStyle({
    explicit: allowedCoachStyle(p?.coachStyle, isPro || proLoading),
    lifestyle: p?.lifestyle,
    behind,
  })
  return t(`coach.style.${style}`, { defaultValue: style })
}

// "Rex・Strict" for the coach nav item: the chosen voice plus its current tone.
export function useCoachNavMeta(): string {
  const { isAuthenticated } = useConvexAuth()
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip')
  const p = prefs as { coachVoice?: string; coachName?: string } | undefined
  const name = coachDisplayName(p?.coachVoice, coachNameOverride(p))
  return `${name}・${useCoachStyleLabel()}`
}
